import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DataTable from 'react-data-table-component'
import { create, differenceBy, isEmpty, toInteger } from 'lodash';
import { Button, MenuItem, Select } from '@mui/material';
import { toggleNoti, toggleError } from 'config/slice/message'
import NoDataComponent from './NoDataComponent';
import SearchComponent from './SearchComponent';
import TableModals from './TableModals';
import { downloadCSV } from 'utils/exportCSV';
import { useDelUserMutation, useGetServiceListMutation, useGetUserListMutation, useUpdateServiceMutation, useUpdateUserMutation } from 'config/api/waV1_user';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { KEY_COL_LABEL_USER_EMAIL, KEY_COL_LABEL_USER_NAME, KEY_COL_LABEL_USER_PHONE, KEY_COL_LABEL_USER_TITLE, KEY_COL_PACKAGE_CONTENT_CN, KEY_COL_PACKAGE_CONTENT_EN, KEY_COL_PACKAGE_CONTENT_TC, KEY_COL_PACKAGE_DISCOUNT, KEY_COL_PACKAGE_DISCOUNT_END, KEY_COL_PACKAGE_DISCOUNT_START, KEY_COL_PACKAGE_NAME, KEY_COL_PACKAGE_PRICE, KEY_ERROR_DELETE_FAILED, KEY_ERROR_FORM_EDIT_FAILED, KEY_LABEL_DELETE, KEY_LABEL_EXPORT_AS, KEY_LABEL_EXPORT_AS_CSV, KEY_LABEL_EXPORT_AS_PDF, KEY_LABEL_EXPORT_FILE_FORMAT, KEY_LABEL_FORM_CANCEL, KEY_LABEL_FORM_CONFIRM, KEY_LABEL_FORM_EDIT, KEY_LABEL_FORM_PIG_MESSAGE, KEY_LABEL_FORM_PIG_PLURAL, KEY_LABEL_FORM_PIG_ROWS_PER_PAGE, KEY_LABEL_FORM_PIG_SELECT_ALL, KEY_LABEL_FORM_PIG_SINGULAR, KEY_LABEL_FORM_TABLE_TITLE, KEY_NOTI_DELETE_SUCC, KEY_NOTI_FORM_EDIT_SUCC, KEY_NOTI_MSG_DELETE, MODULE_PREVIEW_ROUTER_FORM } from 'config/const/locale';
import { format } from 'date-fns';


const IN = ['content_cn', 'content_en', 'content_tc', 'discount', 'discount_end_at', 'discount_start_at', 'price']

// table data
const tableData = []

function createRow(id, name, price, content_tc, content_cn, content_en, discount, discount_start_at, discount_end_at, extra) {
    return { id, name, price, content_tc, content_cn, content_en, discount, discount_start_at, discount_end_at, extra }
}

/**
 * react-data-table-component for ref: https://www.npmjs.com/package/react-data-table-component/v/6.11.8#header-context-menu-when-using-selectable-rows
 * export pdf for ref: jspdf(http://raw.githack.com/MrRio/jsPDF/master/docs/index.html) and jspdf-autotable(https://github.com/simonbengtsson/jsPDF-AutoTable)
 * @Todo i18n
 */
export default function CMSTableWithPackage() {

    const { t } = useTranslation(MODULE_PREVIEW_ROUTER_FORM)
    const { lng } = useSelector(state => state.global)

    // columns definition
    const columns = [
        {
            id: 'id',
            name: 'id',
            selector: row => row.id,
            sortFunction: (a, b) => b?.id - a?.id,
            sortable: true,
            width: '64px',
        },
        {
            id: 'name',
            name: t(KEY_COL_PACKAGE_NAME),
            selector: row => row.name,
        },
        {
            id: 'price',
            name: t(KEY_COL_PACKAGE_PRICE),
            selector: row => row.price,
            maxWidth: '64px'
        },
        {
            id: 'content_tc',
            name: t(KEY_COL_PACKAGE_CONTENT_TC),
            selector: row => row.content_tc,
            maxWidth: '256px',
            wrap: true
        },
        {
            id: 'content_cn',
            name: t(KEY_COL_PACKAGE_CONTENT_CN),
            selector: row => row.content_cn,
            maxWidth: '256px',
            wrap: true
        },
        {
            id: 'content_en',
            name: t(KEY_COL_PACKAGE_CONTENT_EN),
            selector: row => row.content_en,
            maxWidth: '256px',
            wrap: true
        },
        {
            id: 'discount',
            name: t(KEY_COL_PACKAGE_DISCOUNT),
            selector: row => row.discount,
            maxWidth: '64px'
        },
        {
            id: 'discount_start_at',
            name: t(KEY_COL_PACKAGE_DISCOUNT_START),
            selector: row => row.discount_start_at,
        },
        {
            id: 'discount_end_at',
            name: t(KEY_COL_PACKAGE_DISCOUNT_END),
            selector: row => row.discount_end_at,
        },

    ];

    const [selectedRows, setSelectedRows] = useState([]); // save the selected row
    const [toggleCleared, setToggleCleared] = useState(false); // indicate whether hiding the context or not
    const [isEditTableData, setIsEditTableData] = useState(false) // indicate the row table's state: editable or uneditable
    const [allRowData, setAllRowData] = useState(tableData); // save the all table data
    let rowData = {} // save the data waiting to change

    const [expandedRows, setExpandedRows] = useState({})
    const [editRowById, setEditRowById] = useState(0)

    const [filterText, setFilterText] = useState(''); // keyword to search
    const [filterType, setFilterType] = useState(columns[0]?.id) // type to search with keyword
    // const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

    const [getUserListFn] = useGetUserListMutation()
    const [updateUserFn] = useUpdateUserMutation()
    const [delUserFn] = useDelUserMutation()
    const [getSListFn] = useGetServiceListMutation()
    const [upSFn] = useUpdateServiceMutation()
    const dispatch = useDispatch()




    // filter results when conditions meet
    let filteredItems = allRowData.filter(
        item => item[filterType] && String(item[filterType]).toLowerCase().includes(filterText.toLowerCase())
    );

    // save row table's input value
    const iptOnChangeHandler =
        (id, iptType) => (event) => {
            rowData = {
                id,
                payload: {
                    ...rowData.payload,
                    [iptType]: event.target.value
                }
            }
        }

    // Table's subHeader
    const subHeaderComponentMemo = useMemo(() => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: "100%" }}>
            {/* <div style={{ display: 'flex' }}> */}
            {/* <Button type="button">create</Button> */}
            {/* <TableModals columns={columns} dataSetter={setAllRowData} /> */}

            {/* <div style={{ paddingLeft: 16, marginLeft: 16, borderLeft: '1px solid black' }}> */}
            {/* <div>
                    <Select
                        variant='filled'
                        value={1}
                        disableUnderline
                        sx={{
                            '&>div': {
                                p: 1.5
                            }
                        }}
                    >
                        <MenuItem value={1} disabled hidden >{t(KEY_LABEL_EXPORT_AS)}</MenuItem>
                        <MenuItem onClick={() => downloadCSV(isEmpty(selectedRows) ? allRowData : selectedRows)}>{t(KEY_LABEL_EXPORT_AS_CSV)}</MenuItem> */}
            {/* <MenuItem onClick={() => exportPdf(selectedRows, allRowData)}>{t(KEY_LABEL_EXPORT_AS_PDF)}</MenuItem> */}
            {/* <MenuItem>SQL</MenuItem> */}
            {/* <MenuItem>JSON</MenuItem> */}
            {/* <MenuItem>XML</MenuItem> */}
            {/* <MenuItem>MS-Excel</MenuItem> */}
            {/* </Select>
                </div>
            </div > */}
            {/* <input placeholder='type keyword to search' onChange={e => setFilterText(e.target.value)} onClear={handleClear} filterText={filterText} /> */}
            < SearchComponent columns={columns} Kword={filterText} saveKeyword={setFilterText} KType={filterType} ChangeKType={setFilterType} />
        </div >
    ), [
        filterText,
        filterType,
        selectedRows,
        allRowData,
        lng
        // resetPaginationToggle
    ]);

    //save the selected rows data to state
    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    useEffect(() => {
        getSListFn()
            .then(
                res => {
                    setAllRowData(
                        new Array(...(res?.data?.data?.services?.data?.filter(i => !i.name.includes('recharge'))))
                            .map(
                                i => createRow(
                                    i.id,
                                    i.locale_name,
                                    i.price,
                                    i.content_tc,
                                    i.content_cn,
                                    i.content_en,
                                    i.discount,
                                    i.discount_start_at,
                                    i.discount_end_at,
                                    i
                                )
                            )
                    )
                }
            )
            .catch(
                reason => console.log(reason)
            )

    }, [])

    // context component includes delete action
    const contextActions = useMemo(() => {
        const handleDelete = () => {

            if (window.confirm(t(KEY_NOTI_MSG_DELETE))) {
                selectedRows.map(
                    i => delUserFn(i.id)
                        .then(res => {
                            if (res.data?.data) {
                                dispatch(toggleNoti({
                                    indicator: true,
                                    msg: t(KEY_NOTI_DELETE_SUCC)
                                }))
                                window.location.reload()
                            } else {
                                dispatch(toggleError({
                                    indicator: true,
                                    msg: t(KEY_ERROR_DELETE_FAILED)
                                }))
                            }
                        })
                        .catch(reason => {
                            dispatch(toggleError({
                                indicator: true,
                                msg: t(KEY_ERROR_DELETE_FAILED)
                            }))
                        })
                        .finally(() => {
                            setToggleCleared(!toggleCleared);
                        })
                )
                // setAllRowData(differenceBy(allRowData, selectedRows, 'id'));
            }
        };

        return (
            <Button
                key="delete"
                variant="danger"
                onClick={handleDelete}
                style={{ backgroundColor: 'red', color: 'white', fontSize: 14, fontWeight: 700 }} >
                {t(KEY_LABEL_DELETE)}
            </Button >
        );
    }, [allRowData, selectedRows, toggleCleared]);

    // save changed row data to state
    const confirmChange = useCallback(() => {
        rowData = {
            ...rowData,
            payload: {
                ...rowData.payload,
                discount: toInteger(rowData.payload.discount),
                discount_start_at: isEmpty(rowData.payload.discount_start_at) ? '' : format(rowData.payload.discount_start_at.toISOString(), 'yyyy-MM-dd 00:00:00'),
                discount_end_at: isEmpty(rowData.payload.discount_end_at) ? '' : format(rowData.payload.discount_end_at.toISOString(), 'yyyy-MM-dd 23:59:59'),
            }
        }

        upSFn(rowData)
            .then(
                res => {
                    if (res.data?.data) {
                        dispatch(toggleNoti({
                            indicator: true,
                            msg: t(KEY_NOTI_FORM_EDIT_SUCC)
                        }))
                        window.location.reload()
                    } else {
                        dispatch(toggleError({
                            indicator: true,
                            msg: t(KEY_ERROR_FORM_EDIT_FAILED)
                        }))
                    }
                }
            )
            .catch(
                reason => dispatch(toggleError({
                    indicator: true,
                    msg: t(KEY_ERROR_FORM_EDIT_FAILED)
                }))
            )
            .finally(() => {
                setIsEditTableData(false)
            })
        // if (isEmpty(rowData) && isEmpty(rowData?.id)) return setIsEditTableData(false)
        // // setAllRowData(pre => pre.map(i => (i.id === rowData.id) ? set(i, rowData.type, rowData.value) : i))
        // setAllRowData(pre => pre.map(i => (i.id === rowData.id) ? { ...i, [rowData?.type]: rowData?.value } : i))
        // setIsEditTableData(false)
    }, [rowData, allRowData])

    /**
     * expanded component ?? bad performance ??
     * @param {Object} data the expanded row data
     */
    const ExpandedComponent = ({ data }) => {

        const arr = []

        const edit = editRowById === expandedRows?.id

        rowData = {
            ...rowData,
            payload: {
                price: data?.price,
                content_tc: data?.content_tc,
                content_cn: data?.content_cn,
                content_en: data?.content_en,
                discount: data?.discount,
                discount_start_at: data?.discount_start_at,
                discount_end_at: data?.discount_end_at,
                ...rowData.payload
            }
        }

        const fn1 = () => {

            for (let datum in data) {
                if (datum === 'id') continue
                if (datum === 'extra') continue
                arr.push(
                    <tr key={datum} style={{ lineHeight: 2 }} >
                        <td><label htmlFor={datum}>{datum}</label></td>
                        <td>
                            {
                                (isEditTableData && edit) ?
                                    datum == 'content_tc'
                                        ? (
                                            <textarea
                                                id={datum}
                                                name={datum}
                                                className='ms-5'
                                                style={{ maxWidth: 500 }}
                                                rows={'8'}
                                                cols={'100'}
                                                defaultValue={data[datum]}

                                                onChange={iptOnChangeHandler(data['id'], datum)}
                                            />
                                        ) :
                                        datum == 'content_cn'
                                            ? (
                                                <textarea
                                                    id={datum}
                                                    name={datum}
                                                    className='ms-5'
                                                    style={{ maxWidth: 500 }}
                                                    rows={'8'}
                                                    cols={'100'}
                                                    defaultValue={data[datum]}

                                                    onChange={iptOnChangeHandler(data['id'], datum)}
                                                />
                                            ) :
                                            datum == 'content_en'
                                                ? (
                                                    <textarea
                                                        id={datum}
                                                        name={datum}
                                                        className='ms-5'
                                                        style={{ maxWidth: 500 }}
                                                        rows={'8'}
                                                        cols={'100'}
                                                        defaultValue={data[datum]}

                                                        onChange={iptOnChangeHandler(data['id'], datum)}
                                                    />
                                                ) :
                                                datum == 'discount_start_at'
                                                    ? (
                                                        <LocalizationProvider dateAdapter={AdapterDayjs} >
                                                            <DatePicker
                                                                className='ms-5'
                                                                onChange={(newValue) => {
                                                                    rowData = {
                                                                        ...rowData, payload: {
                                                                            ...rowData.payload,
                                                                            discount_start_at: newValue
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        </LocalizationProvider>
                                                    ) :
                                                    datum == 'discount_end_at'
                                                        ? (
                                                            <LocalizationProvider dateAdapter={AdapterDayjs} >
                                                                <DatePicker
                                                                    className='ms-5'
                                                                    onChange={(newValue) => {
                                                                        rowData = {
                                                                            ...rowData, payload: {
                                                                                ...rowData.payload,
                                                                                discount_end_at: newValue
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                            </LocalizationProvider>
                                                        ) :
                                                        datum == 'name'
                                                            ? (
                                                                <div className='ms-5' style={{ maxWidth: 500 }}>{data[datum]}</div>
                                                            )
                                                            : (
                                                                <input
                                                                    id={datum}
                                                                    name={datum}
                                                                    type="text"
                                                                    className='ms-5'
                                                                    style={{ maxWidth: 500 }}
                                                                    defaultValue={data[datum]}

                                                                    onChange={iptOnChangeHandler(data['id'], datum)}
                                                                />
                                                            ) : (
                                        <div className='ms-5' style={{ maxWidth: 500 }}>{data[datum]}</div>
                                    )
                            }

                        </td>
                    </tr>
                )
            }
        }

        const editRowTableHandler = () => { setIsEditTableData(!isEditTableData); setEditRowById(data?.id) }

        fn1()

        return (
            <table style={{ margin: "20px auto" }}>
                <tbody>
                    {arr}
                </tbody>
                <tfoot>
                    <tr>
                        <td style={{ paddingTop: 8 }}>
                            <Button
                                variant='info'
                                style={{ backgroundColor: 'lightgray' }}
                                onClick={editRowTableHandler}
                            >
                                {(isEditTableData && edit) ? t(KEY_LABEL_FORM_CANCEL) : t(KEY_LABEL_FORM_EDIT)}
                            </Button>
                        </td>
                        <td>
                            {
                                (isEditTableData && edit) &&
                                <Button
                                    variant='info'
                                    className='ms-5'
                                    style={{ backgroundColor: 'lightgray' }}
                                    onClick={confirmChange}
                                >
                                    {t(KEY_LABEL_FORM_CONFIRM)}
                                </Button>
                            }
                        </td>
                    </tr>
                </tfoot>
            </table>
        )
    }

    // const actionsMemo = useMemo(() => <Export onExport={() => downloadCSV(data)} />, []);


    const handleChange = ({ allSelected, selectedCount, selectedRows }) => {
        // You can set state or dispatch with something like Redux so we can use the retrieved data
        console.log('Selected Rows: ', selectedRows);
    };

    // export pdf func waiting to optimize
    const exportPdf = useCallback(() => {
        import('jspdf').then((jsPDF) => {
            import('jspdf-autotable').then(() => {
                const doc = new jsPDF.default(0, 0);

                const exportColumns = columns.map((col) => ({ title: col.name, dataKey: col.id }));

                doc.autoTable(exportColumns, isEmpty(selectedRows) ? allRowData : selectedRows);
                doc.save(t(KEY_LABEL_EXPORT_FILE_FORMAT));
            });
        });
    }, [columns, selectedRows, allRowData])


    return (
        <DataTable
            title={t(KEY_LABEL_FORM_TABLE_TITLE)}

            subHeader
            subHeaderComponent={subHeaderComponentMemo}

            columns={columns}
            data={filteredItems}

            // selected row
            selectableRows
            selectableRowsHighlight
            selectableRowsVisibleOnly
            onSelectedRowsChange={handleRowSelected}
            clearSelectedRows={toggleCleared}

            // expanded row
            expandableRows
            expandableRowsComponent={ExpandedComponent}
            expandOnRowDoubleClicked
            expandableRowExpanded={(row) => expandedRows?.id === row.id ? true : false}
            onRowExpandToggled={(toggleState, row) => {
                // console.log(toggleState, row)
                if (toggleState) setExpandedRows(row)
            }}

            // sorting setting
            defaultSortFieldId={'id'}
            defaultSortAsc

            // context setting
            // contextComponent={<div>hello</div>}
            // contextActions={contextActions}
            contextMessage={{
                singular: t(KEY_LABEL_FORM_PIG_SINGULAR),
                plural: t(KEY_LABEL_FORM_PIG_PLURAL),
                message: t(KEY_LABEL_FORM_PIG_MESSAGE)
            }}

            pagination
            paginationRowsPerPageOptions={[10, 20, 50, 100]}
            paginationComponentOptions={{
                rowsPerPageText: t(KEY_LABEL_FORM_PIG_ROWS_PER_PAGE),
                rangeSeparatorText: '/',
                noRowsPerPage: false,
                selectAllRowsItem: true,
                selectAllRowsItemText: t(KEY_LABEL_FORM_PIG_SELECT_ALL)
            }}

            noDataComponent={<NoDataComponent />}

            highlightOnHover
            striped
            responsive


        />
    )
}
